import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button, useAccessControl } from '@plerionhq/design-system';
import { Select } from '@plerionhq/design-system/experimental';
import './access-grant-audit-tab.css';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  useUpdateGrant,
  type UpdateGrantBody,
} from '../../api/use-update-grant';
import { reviewDecisionLabels, trustLapseReasonLabels } from '../../label-maps';
import {
  decisionOptionsFor,
  isReviewLaterWithoutDate,
  isTrustWithoutDate,
  toDecision,
} from './decision-options';
import { ReviewDatePresets } from './review-date-presets';
import {
  defaultNextReviewDateInput,
  formatReviewDate,
  fromDateInput,
  toDateInputValue,
  trustWindowBounds,
} from '../../lib/review-date';
import type { AccessGrant, ReviewDecision, ReviewEvent } from '../../types';

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

dayjs.extend(relativeTime);

const labelClass =
  'mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400';
// Border kept separate so the invalid state can swap it without relying on
// class order, which Tailwind decides in the stylesheet rather than here.
const fieldBase =
  'w-full rounded-md border bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:ring-1 focus:outline-none dark:bg-gray-900 dark:text-gray-100';
const fieldBorder =
  'border-gray-300 focus:border-action-primary focus:ring-action-primary dark:border-gray-600';
const fieldBorderInvalid =
  'border-red-500 focus:border-red-500 focus:ring-red-500 dark:border-red-500';
const fieldClass = `${fieldBase} ${fieldBorder}`;

// The human-readable changes an event recorded — only the fields it touched.
// A present key with a null value reads as "cleared".
//
// Keys are read off the event with `in` rather than a truthiness check, because
// absent and null mean different things here. Note that `in` also happily
// narrows a key the type does not have, so a renamed key compiles clean and
// silently stops rendering — keep these in step with ReviewEvent by hand.
function describeChanges(
  t: TFunction<'accessGrants'>,
  event: ReviewEvent,
): string[] {
  const parts: string[] = [];
  if ('decision' in event) {
    parts.push(
      event.decision
        ? t('decisionChanged', {
            decision: reviewDecisionLabels(t)[event.decision],
          })
        : t('decisionCleared'),
    );
  }
  if ('grantee' in event) {
    parts.push(
      event.grantee
        ? t('granteeChanged', { grantee: event.grantee })
        : t('granteeCleared'),
    );
  }
  if ('nextReviewAt' in event) {
    parts.push(
      event.nextReviewAt
        ? t('nextReviewChanged', {
            date: formatReviewDate(event.nextReviewAt),
          })
        : t('nextReviewCleared'),
    );
  }
  // A lapse is written by the scan with reviewedBy null, so without this the
  // entry renders as a dot and a byline with no text at all.
  if ('trustedUntil' in event) {
    if (event.trustedUntil) {
      parts.push(
        t('trustedUntilDate', { date: formatReviewDate(event.trustedUntil) }),
      );
    } else {
      // Naming the reason is the point: a grant widened after it was trusted
      // reads identically to one whose date simply passed otherwise.
      parts.push(
        event.trustLapseReason
          ? t('trustEndedWithReason', {
              reason:
                trustLapseReasonLabels(t)[event.trustLapseReason].toLowerCase(),
            })
          : t('trustEnded'),
      );
    }
  }
  return parts;
}

// Why a trust date was refused. Named rather than "invalid date" so the reviewer
// knows whether to move the date forward or pull it back.
function dateError(
  t: TFunction<'accessGrants'>,
  reviewDate: string,
  bounds: { min: string; max: string },
): string {
  if (!reviewDate) {
    return t('trustUntilReviewNeedsADate');
  }
  return reviewDate < bounds.min
    ? t('aTrustWindowMustEndInTheFuture')
    : t('aTrustWindowCannotRunPast', { date: formatReviewDate(bounds.max) });
}

function HistoryEntry({ event }: { event: ReviewEvent }) {
  const { t } = useTranslation('accessGrants');
  const parts = describeChanges(t, event);
  const commentChanged = 'comment' in event;
  return (
    <li className="relative pb-4 pl-5 last:pb-0">
      <span className="bg-action-primary absolute top-1 left-0 h-2.5 w-2.5 -translate-x-1/2 rounded-full ring-2 ring-white dark:ring-gray-800" />
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
          {event.reviewedBy ?? t('system')}
        </span>
        <span
          className="text-xs text-gray-400"
          title={new Date(event.reviewedAt).toLocaleString()}
        >
          {dayjs(event.reviewedAt).fromNow()}
        </span>
      </div>
      {parts.length > 0 && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {parts.join(' · ')}
        </div>
      )}
      {commentChanged &&
        (event.comment ? (
          <p className="mt-1 text-sm text-gray-700 italic dark:text-gray-300">
            “{event.comment}”
          </p>
        ) : (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('commentCleared')}
          </p>
        ))}
    </li>
  );
}

type AccessGrantAuditTabProperties = {
  grant: AccessGrant;
};

export function AccessGrantAuditTab({ grant }: AccessGrantAuditTabProperties) {
  const { t } = useTranslation('accessGrants');
  const { mutate, status } = useUpdateGrant();
  const { can } = useAccessControl();
  const canReview = can({
    action: 'AccessGrant:Review',
    resource: grant.integrationId,
  });

  // The slide-over opens with a static snapshot. `current` is the last-saved
  // state, re-seeded from each mutation response (which carries the freshly
  // appended history); the fields below are local edit state. Unlike the inline
  // table cells, the slide-over does NOT autosave — the reviewer edits every
  // field then hits Save once, so the whole review lands as a SINGLE history
  // entry rather than one entry per field.
  const [current, setCurrent] = useState<AccessGrant>(grant);
  const [grantee, setGrantee] = useState(current.grantee ?? '');
  const [decision, setDecision] = useState<ReviewDecision | ''>(
    current.reviewDecision ?? '',
  );
  const [comment, setComment] = useState(current.reviewComment ?? '');
  const [reviewDate, setReviewDate] = useState(
    toDateInputValue(current.nextReviewAt),
  );

  // A patch of ONLY the fields changed vs. the last-saved snapshot, so the
  // recorded history event lists exactly what this review touched. An empty
  // patch means nothing changed — Save stays disabled.
  const trimmedGrantee = grantee.trim();
  const trimmedComment = comment.trim();
  const patch: UpdateGrantBody = {};
  if (trimmedGrantee !== (current.grantee ?? '')) {
    patch.grantee = trimmedGrantee || null;
  }
  if ((decision || null) !== (current.reviewDecision ?? null)) {
    patch.reviewDecision = decision || null;
  }
  if (trimmedComment !== (current.reviewComment ?? '')) {
    patch.reviewComment = trimmedComment || null;
  }
  if (reviewDate !== toDateInputValue(current.nextReviewAt)) {
    patch.nextReviewAt = fromDateInput(reviewDate);
  }
  const isDirty = Object.keys(patch).length > 0;

  // Only reachable by clearing the date after picking Review later, since
  // picking it fills the date in. Blocks the save rather than quietly storing
  // a "later" that never comes.
  const missingReviewDate = isReviewLaterWithoutDate(
    decision || null,
    reviewDate || null,
  );

  // Trusting is bounded — future only, and no further out than the API's trust
  // horizon — because the date IS the trust window. This form batches the
  // decision and the date into one save, so it needs no popover: it just has to
  // refuse to submit a window the API would reject.
  const isTrusting = decision === 'trust_until_review';
  const trustBounds = trustWindowBounds();
  const invalidTrustDate =
    isTrusting &&
    (isTrustWithoutDate(decision, reviewDate || null) ||
      reviewDate < trustBounds.min ||
      reviewDate > trustBounds.max);

  const canSave = isDirty && !missingReviewDate && !invalidTrustDate;
  const isDateInvalid = missingReviewDate || invalidTrustDate;

  const handleDecisionSelect = ({ value }: { value: string }) => {
    const next = toDecision(value);
    setDecision(next ?? '');
    // Same default as the inline table cell, but here it only fills the field
    // in — this form saves on the button, not on change.
    //
    // Trusting deliberately gets no default: how long a grant may go without review
    // is the reviewer's call, and a pre-filled window is one they never made.
    if (isReviewLaterWithoutDate(next, reviewDate || null)) {
      setReviewDate(defaultNextReviewDateInput());
    }
  };

  const handleSave = () => {
    if (!canSave) {
      return;
    }
    mutate(
      { id: current.id, ...patch },
      {
        onSuccess: (updated) => {
          // Re-seed from the persisted row so the form reflects exactly what
          // saved (e.g. trimmed values) and dirtiness resets to clean.
          setCurrent(updated);
          setGrantee(updated.grantee ?? '');
          setDecision(updated.reviewDecision ?? '');
          setComment(updated.reviewComment ?? '');
          setReviewDate(toDateInputValue(updated.nextReviewAt));
        },
      },
    );
  };

  const history = current.reviewHistory ?? [];

  return (
    <div className="flex flex-col gap-6">
      {/* The review form only: a caller without AccessGrant:Review on this
          grant's integration cannot save any of it, and the Save button was
          offered to everyone. The history below stays visible -- reading a
          grant needs only AccessGrant:Read. */}
      {canReview && (
        <section>
          <h4 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('review')}
          </h4>

          {/* Grantee takes half the width; decision and next review a quarter each. */}
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-2">
              <label className={labelClass} htmlFor="grant-grantee">
                {t('grantee')}
              </label>
              <input
                id="grant-grantee"
                className={fieldClass}
                value={grantee}
                placeholder={t('teamOrPersonAccountable')}
                onChange={(e) => setGrantee(e.target.value)}
              />
            </div>
            <div>
              {/* Not a <label>: Select renders a button with no id to point at. */}
              <span className={labelClass}>{t('decision')}</span>
              <Select
                options={decisionOptionsFor(
                  t,
                  current.grantOrigin,
                  current.reviewDecision,
                )}
                value={decision}
                onSelect={handleDecisionSelect}
                // The slide-over body scrolls; portaled options escape it.
                isFixedPositionOptions
                // Select's own padding leaves it 4px shorter than the inputs
                // either side of it — see access-grant-audit-tab.css.
                className="audit-decision-select"
                data-testid="grant-decision"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <label className={labelClass} htmlFor="grant-next-review">
                  {t('nextReview')}
                </label>
                {reviewDate && (
                  <button
                    type="button"
                    aria-label={t('clearNextReviewDate')}
                    onClick={() => setReviewDate('')}
                    className="mb-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="mb-1.5">
                <ReviewDatePresets
                  value={reviewDate}
                  onChange={setReviewDate}
                />
              </div>
              <input
                id="grant-next-review"
                type="date"
                className={`${fieldBase} ${isDateInvalid ? fieldBorderInvalid : fieldBorder}`}
                value={reviewDate}
                min={isTrusting ? trustBounds.min : undefined}
                max={isTrusting ? trustBounds.max : undefined}
                onChange={(e) => setReviewDate(e.target.value)}
                aria-invalid={isDateInvalid}
                aria-describedby={
                  isDateInvalid ? 'grant-next-review-error' : undefined
                }
              />
              {isDateInvalid && (
                <p
                  id="grant-next-review-error"
                  className="mt-1 text-xs text-red-600 dark:text-red-400"
                >
                  {missingReviewDate
                    ? t('reviewLaterNeedsADate')
                    : dateError(t, reviewDate, trustBounds)}
                </p>
              )}
            </div>
          </div>

          {isTrusting && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t('fromTheNextScanThisGrantStopsCounting')}
              {current.trustLapseReason === 'content_changed' &&
                ' ' + t('thePermissionsChangedSinceItWasLastTrusted')}
            </p>
          )}

          <div className="mt-4">
            <label className={labelClass} htmlFor="grant-comment">
              {t('comment')}
            </label>
            <textarea
              id="grant-comment"
              rows={2}
              className={fieldClass}
              value={comment}
              placeholder={t('whyDoesThisAccessExistRecordedIn')}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              variant="primary"
              size="small"
              label={t('saveReview')}
              queryStatus={status}
              isDisabled={!canSave}
              onClick={handleSave}
            />
          </div>
        </section>
      )}
      <section>
        <h4 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('accessReviewHistory')}
        </h4>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('noReviewActivityYetSavingTheFields')}
          </p>
        ) : (
          <ol className="relative ml-1 border-l border-gray-200 dark:border-gray-700">
            {[...history].reverse().map((event, index) => (
              <HistoryEntry
                key={`${event.reviewedAt}-${index}`}
                event={event}
              />
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
